import { Fragment, useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../../contexts/authContext";
import { listDirectory, uploadFile, downloadFile, deleteFile, createFolder } from "./api";

const BUCKET = window.location.hostname.split(".")[0];

function formatBytes(bytes) {
  if (bytes == null) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0, size = bytes;
  while (size >= 1024 && i < units.length - 1) { size /= 1024; i++; }
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleString();
}

export default function S3Page() {
  const { userData } = useAuth();
  const token = userData?.accessToken;

  const [currentPath, setCurrentPath] = useState("");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  const fullPath = currentPath;

  const loadDirectory = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const data = await listDirectory(fullPath, token);
      setEntries(data.Entries || []);
    } catch (err) {
      setError(err.message);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [fullPath, token]);

  useEffect(() => { loadDirectory(); }, [loadDirectory]);

  const handleNavigate = (name) => setCurrentPath((prev) => prev + name + "/");

  const handleBreadcrumb = (index) => {
    const parts = currentPath.split("/").filter(Boolean);
    setCurrentPath(parts.slice(0, index).join("/") + (index > 0 ? "/" : ""));
  };

  const handleUpload = async (files) => {
    for (const file of files) {
      setUploadProgress({ name: file.name, percent: 0 });
      try {
        await uploadFile(fullPath, file, token, (p) => setUploadProgress({ name: file.name, percent: p }));
      } catch (err) {
        setError(`Upload failed: ${err.message}`);
      }
    }
    setUploadProgress(null);
    loadDirectory();
  };

  const handleDownload = async (entry) => {
    const name = entry.FullPath.split("/").pop();
    try {
      await downloadFile(fullPath + name, name, token);
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) return;
    const entry = showDeleteConfirm;
    const name = entry.FullPath.split("/").pop();
    const isDir = (entry.Mode & 0x80000000) !== 0;
    const deletePath = fullPath + name + (isDir ? "/?recursive=true" : "");
    try {
      await deleteFile(deletePath, token);
      loadDirectory();
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    } finally {
      setShowDeleteConfirm(null);
    }
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await createFolder(fullPath + name + "/", token);
      setShowNewFolder(false);
      setNewFolderName("");
      loadDirectory();
    } catch (err) {
      setError(`Create folder failed: ${err.message}`);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = () => setDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length > 0) handleUpload(e.dataTransfer.files);
  };

  if (!token) return <div style={{ padding: 24 }}>Please log in to access files.</div>;

  const breadcrumbParts = currentPath.split("/").filter(Boolean);
  const st = { pad: { padding: 24 }, crumb: { display: "flex", gap: 4, alignItems: "center", marginBottom: 16, fontSize: 14 }, crumbLink: { color: "#4f46e5", cursor: "pointer" },
    dropzone: { border: `2px dashed ${dragging ? "#4f46e5" : "#ccc"}`, borderRadius: 8, padding: 24, textAlign: "center", marginBottom: 16, cursor: "pointer", background: dragging ? "#f0f0ff" : "#fafafa", color: "#888" },
    row: { display: "grid", gridTemplateColumns: "1fr 100px 160px 80px", padding: "8px 0", borderBottom: "1px solid #eee", alignItems: "center" },
    header: { fontWeight: 600, fontSize: 13, color: "#888" },
    link: { color: "#4f46e5", cursor: "pointer", textDecoration: "none" },
    btn: { padding: "4px 12px", border: "1px solid #ccc", borderRadius: 4, background: "#fff", cursor: "pointer", fontSize: 13 },
    btnDanger: { padding: "4px 12px", border: "none", borderRadius: 4, background: "#ef4444", color: "#fff", cursor: "pointer", fontSize: 13 },
    modal: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
    modalBox: { background: "#fff", borderRadius: 12, padding: 24, minWidth: 320 },
    progress: { height: 6, background: "#e5e7eb", borderRadius: 3, marginTop: 8 },
    progressFill: { height: 6, background: "#4f46e5", borderRadius: 3, transition: "width 0.2s" },
    error: { padding: 12, background: "#fee", color: "#c00", borderRadius: 8, marginBottom: 16 },
    empty: { padding: 40, textAlign: "center", color: "#888" },
  };

  return (
    <div style={st.pad}>
      <h2 style={{ marginBottom: 16 }}>File Browser</h2>

      <div style={st.crumb}>
        <span style={st.crumbLink} onClick={() => handleBreadcrumb(0)}>{BUCKET}</span>
        {breadcrumbParts.map((part, i) => (
          <Fragment key={i}>
            <span style={{ color: "#ccc" }}>/</span>
            <span style={st.crumbLink} onClick={() => handleBreadcrumb(i + 1)}>{part}</span>
          </Fragment>
        ))}
      </div>

      <div style={st.dropzone} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()}>
        <input ref={fileInputRef} type="file" multiple style={{ display: "none" }} onChange={(e) => handleUpload(e.target.files)} />
        Drop files here or click to upload
        {uploadProgress && (
          <div style={{ marginTop: 12 }}>
            <div>{uploadProgress.name} — {uploadProgress.percent}%</div>
            <div style={st.progress}><div style={{ ...st.progressFill, width: `${uploadProgress.percent}%` }} /></div>
          </div>
        )}
      </div>

      <div style={{ marginBottom: 12, display: "flex", gap: 8 }}>
        <button style={st.btn} onClick={() => setShowNewFolder(true)}>New Folder</button>
        <button style={st.btn} onClick={loadDirectory}>Refresh</button>
      </div>

      {error && <div style={st.error}>{error}</div>}

      <div>
        <div style={{ ...st.row, ...st.header }}><span>Name</span><span>Size</span><span>Modified</span><span></span></div>
        {loading && <div style={{ padding: 16, color: "#888" }}>Loading...</div>}
        {!loading && entries.length === 0 && <div style={st.empty}><div style={{ fontSize: 40 }}>📁</div><p>This folder is empty</p></div>}
        {!loading && entries.map((entry) => {
          const name = entry.FullPath.split("/").pop();
          const isDir = (entry.Mode & 0x80000000) !== 0;
          return (
            <div style={st.row} key={entry.FullPath}>
              <div>
                <span style={{ marginRight: 8 }}>{isDir ? "📁" : "📄"}</span>
                <span style={st.link} onClick={() => isDir ? handleNavigate(name) : handleDownload(entry)}>{name}</span>
              </div>
              <span style={{ fontSize: 13, color: "#888" }}>{isDir ? "" : formatBytes(entry.FileSize)}</span>
              <span style={{ fontSize: 13, color: "#888" }}>{formatDate(entry.Mtime)}</span>
              <div style={{ display: "flex", gap: 4 }}>
                {!isDir && <button style={st.btn} title="Download" onClick={() => handleDownload(entry)}>⬇</button>}
                <button style={st.btn} title="Delete" onClick={() => setShowDeleteConfirm(entry)}>🗑</button>
              </div>
            </div>
          );
        })}
      </div>

      {showDeleteConfirm && (
        <div style={st.modal} onClick={() => setShowDeleteConfirm(null)}>
          <div style={st.modalBox} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: 12 }}>Confirm Delete</h3>
            <p>Delete <strong>{showDeleteConfirm.FullPath.split("/").pop()}</strong>?</p>
            <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button style={st.btn} onClick={() => setShowDeleteConfirm(null)}>Cancel</button>
              <button style={st.btnDanger} onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {showNewFolder && (
        <div style={st.modal} onClick={() => setShowNewFolder(false)}>
          <div style={st.modalBox} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: 12 }}>New Folder</h3>
            <input type="text" placeholder="Folder name" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)}
              autoFocus onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
              style={{ width: "100%", padding: 8, border: "1px solid #ccc", borderRadius: 4, fontSize: 14 }} />
            <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button style={st.btn} onClick={() => setShowNewFolder(false)}>Cancel</button>
              <button style={{ ...st.btn, background: "#4f46e5", color: "#fff", border: "none" }} onClick={handleCreateFolder}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
